import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import StocksAtelierPage from '@/pages/StocksAtelierPage';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Switch } from '@/components/ui/switch';
import { 
  Package, Plus, Minus, AlertTriangle, TrendingDown, TrendingUp, 
  Fuel, FlaskConical, Leaf, Grape, Wrench, Sprout, 
  Search, Filter, RefreshCw, Loader2, MoreVertical,
  ArrowDownCircle, ArrowUpCircle, History, Bell, Settings,
  Warehouse, MapPin, Calendar, DollarSign, Trash2, Edit,
  Upload, FileCheck, Award
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import api from '@/lib/api';
import { useWorkspace } from '@/contexts/WorkspaceContext';

// Icônes par catégorie
const CATEGORY_ICONS = {
  carburant: Fuel,
  phyto: FlaskConical,
  intrant: Leaf,
  production: Grape,
  piece: Wrench,
  plant: Sprout
};

const CATEGORY_COLORS = {
  carburant: 'bg-amber-500',
  phyto: 'bg-red-500',
  intrant: 'bg-green-500',
  production: 'bg-purple-500',
  piece: 'bg-gray-500',
  plant: 'bg-emerald-500'
};

// Composant carte catégorie avec animation
const CategoryCard = ({ category, data, onClick, isSelected }) => {
  const Icon = CATEGORY_ICONS[category] || Package;
  const colorClass = CATEGORY_COLORS[category] || 'bg-gray-500';
  
  return (
    <Card 
      className={cn(
        "cursor-pointer transition-all hover:scale-105 hover:shadow-lg",
        isSelected && "ring-2 ring-primary ring-offset-2"
      )}
      onClick={() => onClick(category)}
      data-testid={`category-${category}`}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className={cn("p-3 rounded-xl", colorClass)}>
            <Icon className="w-6 h-6 text-white" />
          </div>
          {data.en_alerte > 0 && (
            <Badge variant="destructive" className="animate-pulse">
              {data.en_alerte} ⚠️
            </Badge>
          )}
        </div>
        <div className="mt-3">
          <h3 className="font-semibold text-sm">{data.nom}</h3>
          <p className="text-2xl font-bold mt-1">{data.total_items}</p>
          <p className="text-xs text-muted-foreground">articles</p>
        </div>
        {data.valeur_totale > 0 && (
          <div className="mt-2 pt-2 border-t">
            <p className="text-xs text-muted-foreground">Valeur</p>
            <p className="font-semibold text-green-600">{data.valeur_totale.toLocaleString()} €</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Composant ligne de stock
const StockRow = ({ stock, onEdit, onMouvement, onDelete }) => {
  const Icon = CATEGORY_ICONS[stock.categorie] || Package;
  const pourcentage = stock.seuil_alerte 
    ? Math.min(100, (stock.quantite / stock.seuil_alerte) * 50) 
    : 100;
  
  return (
    <div className={cn(
      "flex items-center gap-4 p-4 rounded-lg border transition-all hover:bg-muted/50",
      stock.en_alerte && "border-red-300 bg-red-50/50 dark:bg-red-900/10"
    )}>
      <div className={cn("p-2 rounded-lg", CATEGORY_COLORS[stock.categorie] || 'bg-gray-500')}>
        <Icon className="w-5 h-5 text-white" />
      </div>
      
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <h4 className="font-medium truncate">{stock.nom}</h4>
          {stock.bio && <Badge variant="outline" className="text-green-600 border-green-600">BIO</Badge>}
          {stock.en_alerte && (
            <Badge variant="destructive" className="animate-pulse">
              <AlertTriangle className="w-3 h-3 mr-1" />
              Stock bas
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-4 mt-1 text-sm text-muted-foreground">
          <span>{stock.sous_categorie || stock.categorie_nom}</span>
          {stock.emplacement && (
            <span className="flex items-center gap-1">
              <MapPin className="w-3 h-3" />
              {stock.emplacement}
            </span>
          )}
          {stock.date_peremption && (
            <span className="flex items-center gap-1">
              <Calendar className="w-3 h-3" />
              DLC: {stock.date_peremption}
            </span>
          )}
        </div>
      </div>
      
      <div className="text-right">
        <p className={cn(
          "text-2xl font-bold",
          stock.en_alerte ? "text-red-600" : "text-foreground"
        )}>
          {stock.quantite}
        </p>
        <p className="text-xs text-muted-foreground">{stock.unite}</p>
        {stock.seuil_alerte && (
          <Progress 
            value={pourcentage} 
            className={cn("h-1 mt-1 w-20", stock.en_alerte && "[&>div]:bg-red-500")}
          />
        )}
      </div>
      
      <div className="flex gap-1">
        <Button 
          size="icon" 
          variant="outline" 
          className="h-8 w-8 text-green-600 hover:bg-green-50"
          onClick={() => onMouvement(stock, 'entree')}
          title="Entrée de stock"
        >
          <ArrowDownCircle className="w-4 h-4" />
        </Button>
        <Button 
          size="icon" 
          variant="outline" 
          className="h-8 w-8 text-red-600 hover:bg-red-50"
          onClick={() => onMouvement(stock, 'sortie')}
          title="Sortie de stock"
        >
          <ArrowUpCircle className="w-4 h-4" />
        </Button>
        <Button 
          size="icon" 
          variant="ghost" 
          className="h-8 w-8"
          onClick={() => onEdit(stock)}
        >
          <Edit className="w-4 h-4" />
        </Button>
        <Button 
          size="icon" 
          variant="ghost" 
          className="h-8 w-8 text-red-600"
          onClick={() => onDelete(stock.id)}
        >
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
};

// Composant alerte
const AlerteCard = ({ alerte }) => (
  <div className={cn(
    "flex items-center gap-3 p-3 rounded-lg",
    alerte.priorite === 'haute' ? "bg-red-100 dark:bg-red-900/20" : "bg-amber-100 dark:bg-amber-900/20"
  )}>
    <span className="text-2xl">{alerte.icone}</span>
    <div className="flex-1">
      <p className="font-medium text-sm">{alerte.titre}</p>
      <p className="text-xs text-muted-foreground">{alerte.message}</p>
    </div>
    <Badge variant={alerte.priorite === 'haute' ? 'destructive' : 'outline'}>
      {alerte.priorite}
    </Badge>
  </div>
);

// Page principale
function StocksProduits({ categorieForcee = null }) {
  const { activeSociete } = useWorkspace();
  const [stocks, setStocks] = useState([]);
  const [stats, setStats] = useState({});
  const [categories, setCategories] = useState({});
  const [alertes, setAlertes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(categorieForcee);
  useEffect(() => { setSelectedCategory(categorieForcee); }, [categorieForcee]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAlertsOnly, setShowAlertsOnly] = useState(false);
  
  // Dialog states
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showMouvementDialog, setShowMouvementDialog] = useState(false);
  const [selectedStock, setSelectedStock] = useState(null);
  const [mouvementType, setMouvementType] = useState('entree');
  
  // Détection société BIO (EARL Vergers Bio)
  const isSocieteBio = activeSociete?.nom?.toLowerCase().includes('bio') || 
                       activeSociete?.nom?.toLowerCase().includes('vergers bio');
  
  // Form states
  const [newStock, setNewStock] = useState({
    nom: '',
    categorie: 'carburant',
    sous_categorie: '',
    quantite: 0,
    unite: 'L',
    seuil_alerte: null,
    prix_unitaire: null,
    fournisseur: '',
    emplacement: '',
    bio: isSocieteBio, // Auto-set to true for BIO companies
    certificat_bio_url: null,
    notes: ''
  });
  const [mouvementQuantite, setMouvementQuantite] = useState(0);
  const [mouvementMotif, setMouvementMotif] = useState('');
  const [bioCertificatFile, setBioCertificatFile] = useState(null);
  const [uploadingCertificat, setUploadingCertificat] = useState(false);
  
  // Fetch data
  const fetchData = async () => {
    try {
      const [stocksRes, alertesRes, categoriesRes] = await Promise.all([
        api.get('/stocks'),
        api.get('/stocks/alertes'),
        api.get('/stocks/categories')
      ]);
      
      setStocks(stocksRes.data.stocks || []);
      setStats(stocksRes.data.stats || {});
      setAlertes(alertesRes.data.alertes || []);
      setCategories(categoriesRes.data.categories || {});
    } catch (error) {
      console.error('Erreur chargement stocks:', error);
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    fetchData();
  }, []);
  
  // Filtrer les stocks
  const filteredStocks = stocks.filter(s => {
    if (selectedCategory && s.categorie !== selectedCategory) return false;
    if (showAlertsOnly && !s.en_alerte) return false;
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return s.nom?.toLowerCase().includes(query) || 
             s.emplacement?.toLowerCase().includes(query) ||
             s.fournisseur?.toLowerCase().includes(query);
    }
    return true;
  });
  
  // Créer un stock
  const handleCreateStock = async () => {
    try {
      // Si produit BIO avec certificat, l'uploader d'abord vers Mon Cerveau
      let certificatUrl = null;
      if (newStock.bio && bioCertificatFile && activeSociete?.id) {
        setUploadingCertificat(true);
        try {
          const formData = new FormData();
          formData.append('societe_id', activeSociete.id);
          formData.append('nom', `Certificat BIO - ${newStock.nom}`);
          formData.append('description', `Certificat de conformité BIO pour le produit "${newStock.nom}"`);
          formData.append('categorie', 'administratif');
          formData.append('tags', 'bio,certificat,controle');
          formData.append('file', bioCertificatFile);
          
          const uploadRes = await api.post('/brain-memory/upload', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          certificatUrl = uploadRes.data?.file_url;
          toast.success('Certificat BIO enregistré dans Mon Cerveau');
        } catch (uploadError) {
          console.error('Erreur upload certificat:', uploadError);
          // On continue quand même la création du stock
        }
        setUploadingCertificat(false);
      }
      
      // Créer le stock avec l'URL du certificat
      const stockData = { ...newStock };
      if (certificatUrl) {
        stockData.certificat_bio_url = certificatUrl;
      }
      
      await api.post('/stocks', stockData);
      toast.success(`Stock "${newStock.nom}" créé`);
      setShowAddDialog(false);
      setNewStock({
        nom: '', categorie: 'carburant', sous_categorie: '', quantite: 0,
        unite: 'L', seuil_alerte: null, prix_unitaire: null, fournisseur: '',
        emplacement: '', bio: isSocieteBio, certificat_bio_url: null, notes: ''
      });
      setBioCertificatFile(null);
      fetchData();
    } catch (error) {
      toast.error('Erreur création');
    }
  };
  
  // Enregistrer mouvement
  const handleMouvement = async () => {
    if (!selectedStock || mouvementQuantite <= 0) return;
    
    try {
      const res = await api.post('/stocks/mouvement', {
        stock_id: selectedStock.id,
        type_mouvement: mouvementType,
        quantite: mouvementQuantite,
        motif: mouvementMotif
      });
      
      if (res.data.alerte) {
        toast.warning(res.data.alerte.message);
      } else {
        toast.success(`${mouvementType === 'entree' ? 'Entrée' : 'Sortie'} enregistrée`);
      }
      
      setShowMouvementDialog(false);
      setMouvementQuantite(0);
      setMouvementMotif('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur mouvement');
    }
  };
  
  // Supprimer stock
  const handleDelete = async (stockId) => {
    if (!confirm('Supprimer ce stock ?')) return;
    
    try {
      await api.delete(`/stocks/${stockId}`);
      toast.success('Stock supprimé');
      fetchData();
    } catch (error) {
      toast.error('Erreur suppression');
    }
  };
  
  // Ouvrir dialog mouvement
  const openMouvementDialog = (stock, type) => {
    setSelectedStock(stock);
    setMouvementType(type);
    setShowMouvementDialog(true);
  };

  if (loading) {
    return (
      <div className="h-[calc(100vh-200px)] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="stocks-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Package className="w-8 h-8" />
            Gestion des Stocks
          </h1>
          <p className="text-muted-foreground">
            Carburants, phytos, intrants, pièces et plants
          </p>
        </div>
        
        <div className="flex gap-2">
          <Button onClick={() => setShowAddDialog(true)} data-testid="btn-add-stock">
            <Plus className="w-4 h-4 mr-2" />
            Nouveau stock
          </Button>
          <Button variant="outline" onClick={fetchData}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Actualiser
          </Button>
        </div>
      </div>
      
      {/* KPIs rapides */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
          <CardContent className="p-4">
            <p className="text-blue-100 text-sm">Total articles</p>
            <p className="text-3xl font-bold">{stats.total_items || stocks.length}</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
          <CardContent className="p-4">
            <p className="text-green-100 text-sm">Valeur totale</p>
            <p className="text-3xl font-bold">{(stats.valeur_totale || 0).toLocaleString()} €</p>
          </CardContent>
        </Card>
        <Card className={cn(
          "text-white",
          alertes.length > 0 
            ? "bg-gradient-to-br from-red-500 to-red-600 animate-pulse" 
            : "bg-gradient-to-br from-gray-500 to-gray-600"
        )}>
          <CardContent className="p-4">
            <p className="text-red-100 text-sm">Alertes actives</p>
            <p className="text-3xl font-bold">{alertes.length}</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
          <CardContent className="p-4">
            <p className="text-purple-100 text-sm">Catégories</p>
            <p className="text-3xl font-bold">{Object.keys(stats).length || 6}</p>
          </CardContent>
        </Card>
      </div>
      
      {/* Alertes */}
      {alertes.length > 0 && (
        <Card className="border-red-200 bg-red-50/50 dark:bg-red-900/10">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2 text-red-600">
              <Bell className="w-5 h-5 animate-bounce" />
              Alertes ({alertes.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
              {alertes.slice(0, 6).map((alerte, idx) => (
                <AlerteCard key={idx} alerte={alerte} />
              ))}
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Catégories */}
      <div>
        <h2 className="text-lg font-semibold mb-3">Catégories</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {Object.entries(categories).map(([catId, catInfo]) => (
            <CategoryCard
              key={catId}
              category={catId}
              data={{
                nom: catInfo.nom,
                icone: catInfo.icone,
                total_items: stocks.filter(s => s.categorie === catId).length,
                en_alerte: stocks.filter(s => s.categorie === catId && s.en_alerte).length,
                valeur_totale: stocks.filter(s => s.categorie === catId)
                  .reduce((sum, s) => sum + (s.quantite * (s.prix_unitaire || 0)), 0)
              }}
              onClick={setSelectedCategory}
              isSelected={selectedCategory === catId}
            />
          ))}
        </div>
      </div>
      
      {/* Liste des stocks */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <CardTitle className="text-lg">
              {selectedCategory 
                ? `${categories[selectedCategory]?.nom || selectedCategory}` 
                : 'Tous les stocks'
              }
              <Badge variant="secondary" className="ml-2">{filteredStocks.length}</Badge>
            </CardTitle>
            
            <div className="flex gap-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Rechercher..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9 w-64"
                />
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={showAlertsOnly}
                  onCheckedChange={setShowAlertsOnly}
                />
                <Label className="text-sm">Alertes seules</Label>
              </div>
              {selectedCategory && (
                <Button variant="ghost" size="sm" onClick={() => setSelectedCategory(null)}>
                  Voir tout
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <ScrollArea className="h-[500px] pr-4">
            <div className="space-y-2">
              {filteredStocks.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Package className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>Aucun stock trouvé</p>
                  <Button 
                    variant="link" 
                    onClick={() => { setSelectedCategory(null); setSearchQuery(''); }}
                  >
                    Réinitialiser les filtres
                  </Button>
                </div>
              ) : (
                filteredStocks.map(stock => (
                  <StockRow
                    key={stock.id}
                    stock={stock}
                    onEdit={(s) => console.log('Edit:', s)}
                    onMouvement={openMouvementDialog}
                    onDelete={handleDelete}
                  />
                ))
              )}
            </div>
          </ScrollArea>
        </CardContent>
      </Card>
      
      {/* Dialog Ajout Stock */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5" />
              Nouveau stock
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Nom *</Label>
                <Input
                  value={newStock.nom}
                  onChange={(e) => setNewStock({...newStock, nom: e.target.value})}
                  placeholder="Ex: GNR Cuve 1"
                />
              </div>
              <div className="space-y-2">
                <Label>Catégorie *</Label>
                <Select 
                  value={newStock.categorie}
                  onValueChange={(v) => setNewStock({...newStock, categorie: v})}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(categories).map(([id, info]) => (
                      <SelectItem key={id} value={id}>
                        {info.icone} {info.nom}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Quantité</Label>
                <Input
                  type="number"
                  value={newStock.quantite}
                  onChange={(e) => setNewStock({...newStock, quantite: parseFloat(e.target.value) || 0})}
                />
              </div>
              <div className="space-y-2">
                <Label>Unité</Label>
                <Select 
                  value={newStock.unite}
                  onValueChange={(v) => setNewStock({...newStock, unite: v})}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="L">Litres (L)</SelectItem>
                    <SelectItem value="kg">Kilogrammes (kg)</SelectItem>
                    <SelectItem value="unité">Unités</SelectItem>
                    <SelectItem value="plants">Plants</SelectItem>
                    <SelectItem value="m">Mètres (m)</SelectItem>
                    <SelectItem value="T">Tonnes (T)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Seuil alerte</Label>
                <Input
                  type="number"
                  value={newStock.seuil_alerte || ''}
                  onChange={(e) => setNewStock({...newStock, seuil_alerte: parseFloat(e.target.value) || null})}
                  placeholder="Min"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Prix unitaire (€)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={newStock.prix_unitaire || ''}
                  onChange={(e) => setNewStock({...newStock, prix_unitaire: parseFloat(e.target.value) || null})}
                />
              </div>
              <div className="space-y-2">
                <Label>Emplacement</Label>
                <Input
                  value={newStock.emplacement}
                  onChange={(e) => setNewStock({...newStock, emplacement: e.target.value})}
                  placeholder="Ex: Hangar A, Cuve 1"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Fournisseur</Label>
              <Input
                value={newStock.fournisseur}
                onChange={(e) => setNewStock({...newStock, fournisseur: e.target.value})}
                placeholder="Nom du fournisseur"
              />
            </div>
            
            <div className="flex items-center gap-2">
              <Switch
                checked={newStock.bio}
                onCheckedChange={(v) => setNewStock({...newStock, bio: v})}
                disabled={isSocieteBio} // Désactivé si société 100% BIO
              />
              <Label className="flex items-center gap-2">
                <Leaf className="w-4 h-4 text-green-600" />
                Produit certifié BIO
                {isSocieteBio && (
                  <Badge variant="outline" className="text-green-600 border-green-600 text-xs">
                    Société 100% BIO
                  </Badge>
                )}
              </Label>
            </div>
            
            {/* Upload certificat BIO si produit BIO */}
            {newStock.bio && (
              <div className="space-y-2 p-4 rounded-lg bg-green-50 dark:bg-green-900/10 border border-green-200 dark:border-green-800">
                <Label className="flex items-center gap-2 text-green-700 dark:text-green-400">
                  <Award className="w-4 h-4" />
                  Certificat BIO (pour les contrôles)
                </Label>
                <div className="border-2 border-dashed border-green-300 rounded-lg p-4 text-center">
                  <input
                    type="file"
                    id="bio-certificat"
                    className="hidden"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => setBioCertificatFile(e.target.files[0])}
                  />
                  <label htmlFor="bio-certificat" className="cursor-pointer">
                    {bioCertificatFile ? (
                      <div className="flex items-center justify-center gap-2 text-green-600">
                        <FileCheck className="w-5 h-5" />
                        <span className="font-medium">{bioCertificatFile.name}</span>
                      </div>
                    ) : (
                      <div className="text-muted-foreground">
                        <Upload className="w-8 h-8 mx-auto mb-2 text-green-500" />
                        <p className="text-sm">Cliquez pour ajouter le certificat</p>
                        <p className="text-xs">PDF, JPG, PNG</p>
                      </div>
                    )}
                  </label>
                </div>
                <p className="text-xs text-muted-foreground">
                  Ce document sera stocké dans votre Cerveau pour les contrôles.
                </p>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>
              Annuler
            </Button>
            <Button onClick={handleCreateStock} disabled={!newStock.nom}>
              <Plus className="w-4 h-4 mr-2" />
              Créer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Dialog Mouvement */}
      <Dialog open={showMouvementDialog} onOpenChange={setShowMouvementDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {mouvementType === 'entree' ? (
                <>
                  <ArrowDownCircle className="w-5 h-5 text-green-600" />
                  Entrée de stock
                </>
              ) : (
                <>
                  <ArrowUpCircle className="w-5 h-5 text-red-600" />
                  Sortie de stock
                </>
              )}
            </DialogTitle>
          </DialogHeader>
          
          {selectedStock && (
            <div className="space-y-4 py-4">
              <div className="p-3 bg-muted rounded-lg">
                <p className="font-medium">{selectedStock.nom}</p>
                <p className="text-sm text-muted-foreground">
                  Stock actuel : {selectedStock.quantite} {selectedStock.unite}
                </p>
              </div>
              
              <div className="space-y-2">
                <Label>Quantité</Label>
                <Input
                  type="number"
                  value={mouvementQuantite}
                  onChange={(e) => setMouvementQuantite(parseFloat(e.target.value) || 0)}
                  min={0}
                  max={mouvementType === 'sortie' ? selectedStock.quantite : undefined}
                />
                {mouvementType === 'sortie' && mouvementQuantite > selectedStock.quantite && (
                  <p className="text-xs text-red-500">Quantité insuffisante !</p>
                )}
              </div>
              
              <div className="space-y-2">
                <Label>Motif</Label>
                <Select value={mouvementMotif} onValueChange={setMouvementMotif}>
                  <SelectTrigger><SelectValue placeholder="Sélectionner..." /></SelectTrigger>
                  <SelectContent>
                    {mouvementType === 'entree' ? (
                      <>
                        <SelectItem value="achat">Achat</SelectItem>
                        <SelectItem value="production">Production</SelectItem>
                        <SelectItem value="transfert">Transfert</SelectItem>
                        <SelectItem value="inventaire">Ajustement inventaire</SelectItem>
                      </>
                    ) : (
                      <>
                        <SelectItem value="intervention">Intervention</SelectItem>
                        <SelectItem value="vente">Vente</SelectItem>
                        <SelectItem value="perte">Perte/Casse</SelectItem>
                        <SelectItem value="transfert">Transfert</SelectItem>
                        <SelectItem value="inventaire">Ajustement inventaire</SelectItem>
                      </>
                    )}
                  </SelectContent>
                </Select>
              </div>
              
              {mouvementType === 'entree' && mouvementQuantite > 0 && (
                <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                  <p className="text-sm text-green-700 dark:text-green-300">
                    Nouveau stock : <strong>{selectedStock.quantite + mouvementQuantite} {selectedStock.unite}</strong>
                  </p>
                </div>
              )}
              
              {mouvementType === 'sortie' && mouvementQuantite > 0 && (
                <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
                  <p className="text-sm text-red-700 dark:text-red-300">
                    Nouveau stock : <strong>{selectedStock.quantite - mouvementQuantite} {selectedStock.unite}</strong>
                  </p>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMouvementDialog(false)}>
              Annuler
            </Button>
            <Button 
              onClick={handleMouvement}
              disabled={mouvementQuantite <= 0 || (mouvementType === 'sortie' && mouvementQuantite > selectedStock?.quantite)}
              className={mouvementType === 'entree' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}
            >
              {mouvementType === 'entree' ? (
                <>
                  <ArrowDownCircle className="w-4 h-4 mr-2" />
                  Enregistrer entrée
                </>
              ) : (
                <>
                  <ArrowUpCircle className="w-4 h-4 mr-2" />
                  Enregistrer sortie
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


const VUES_STOCKS = [
  { id: 'produits', label: 'Produits', categorie: null },
  { id: 'phyto', label: 'Phyto', categorie: 'phyto' },
  { id: 'atelier', label: 'Atelier' },
];

export default function StocksPage() {
  const [vue, setVue] = useState('produits');
  const courante = VUES_STOCKS.find((v) => v.id === vue);
  return (
    <div className="space-y-4">
      <div className="inline-flex rounded-full border bg-white p-1 gap-1" data-testid="stocks-filtre-vue">
        {VUES_STOCKS.map((v) => (
          <button
            key={v.id}
            type="button"
            data-testid={`stocks-vue-${v.id}`}
            onClick={() => setVue(v.id)}
            className={cn('px-4 py-1.5 rounded-full text-sm transition-colors', vue === v.id ? 'bg-emerald-700 text-white' : 'text-muted-foreground hover:bg-muted')}
          >
            {v.label}
          </button>
        ))}
      </div>
      {vue === 'atelier' ? <StocksAtelierPage /> : <StocksProduits categorieForcee={courante.categorie} />}
    </div>
  );
}
